<script setup lang="ts">
import IconButton from '@/core/elements/IconButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { nextTick, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  open: boolean;
  title: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const closeButtonRef = useTemplateRef<InstanceType<typeof IconButton>>('closeButtonRef');

watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    await nextTick();
    closeButtonRef.value?.$el?.focus();
  },
);
</script>

<template>
  <button
    v-if="open"
    type="button"
    class="absolute inset-0 z-40 hidden bg-primary/10 max-md:block"
    :aria-label="$t('general.close')"
    @click="emit('close')"
  />

  <aside
    class="absolute inset-y-0 left-0 z-50 hidden w-[calc(100%-46px)] max-w-[356px] flex-col overflow-hidden bg-surface-primary px-md pb-mobile-safe-bottom-md pt-[env(safe-area-inset-top)] shadow-floating-panel transition-transform duration-300 max-md:flex"
    :class="open ? 'translate-x-0' : '-translate-x-full pointer-events-none'"
    :aria-hidden="!open"
    :inert="!open"
    :aria-label="title"
    aria-modal="true"
    role="dialog"
  >
    <header class="flex h-[72px] shrink-0 items-center justify-between py-md pl-lg pr-smd">
      <h2 class="truncate text-title font-medium leading-[1.65] tracking-[0.01em] text-primary">
        {{ title }}
      </h2>
      <IconButton
        ref="closeButtonRef"
        type="button"
        size="button"
        :icon-name="EIcon.X"
        :aria-label="$t('general.close')"
        theme="text"
        @click="emit('close')"
      />
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-sm">
      <slot />
    </div>
  </aside>
</template>
